import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Shield, Database, Users, FileText, AlertTriangle,
  CheckCircle, Clock, Bell, TrendingUp, Eye, Trash2,
  BarChart3, Loader2, RefreshCw, Download, UserCheck,
  Timer, FileWarning, Send, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { getDPDPADashboard, seedDefaults, downloadComplianceReport, getDPODashboard } from '@/services/dpdpa';

const ScoreGauge = ({ score }) => {
  const color = score >= 80 ? 'text-emerald-500' : score >= 60 ? 'text-amber-500' : 'text-red-500';
  const bg = score >= 80 ? 'bg-emerald-500/10' : score >= 60 ? 'bg-amber-500/10' : 'bg-red-500/10';
  return (
    <div className={`flex flex-col items-center justify-center p-6 rounded-xl ${bg}`} data-testid="compliance-score-gauge">
      <span className={`text-5xl font-bold ${color}`}>{score}%</span>
      <span className="text-sm mt-1 opacity-70">DPDPA Compliance</span>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color = 'text-blue-500', testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-wider opacity-60 mb-1">{label}</p>
          <p className="text-2xl font-bold">{value}</p>
          {sub && <p className="text-xs opacity-50 mt-1">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg ${color.replace('text-', 'bg-')}/10`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function DPDPADashboard() {
  const [data, setData] = useState(null);
  const [dpoData, setDpoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dpoLoading, setDpoLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getDPDPADashboard();
      setData(result);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDPO = useCallback(async () => {
    try {
      setDpoLoading(true);
      const result = await getDPODashboard();
      setDpoData(result);
    } catch (err) {
      console.error('DPO dashboard fetch error:', err);
    } finally {
      setDpoLoading(false);
    }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);
  useEffect(() => { if (activeTab === 'dpo') fetchDPO(); }, [activeTab, fetchDPO]);

  const handleSeed = async () => {
    try {
      setSeeding(true);
      const result = await seedDefaults();
      toast.success(result.message || 'Default data seeded');
      fetchDashboard();
    } catch (err) {
      toast.error('Failed to seed defaults');
    } finally {
      setSeeding(false);
    }
  };

  const handleDownloadReport = async () => {
    try {
      toast.info('Generating PDF report...');
      const blob = await downloadComplianceReport();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DPDPA_Compliance_Report_${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (err) {
      toast.error('Failed to generate report');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="dpdpa-dashboard-loading">
        <Loader2 className="w-8 h-8 animate-spin opacity-50" />
      </div>
    );
  }

  const showEmpty = !data || (data.purposes?.active === 0 && data.consents?.total === 0 && data.requests?.total === 0);

  return (
    <div className="p-6 space-y-6" data-testid="dpdpa-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">DPDPA Consent Manager</h1>
          <p className="text-sm opacity-60">Digital Personal Data Protection Act, 2023 — Compliance Dashboard</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleDownloadReport} variant="outline" data-testid="download-report-btn">
            <Download className="w-4 h-4 mr-2" /> Export PDF Report
          </Button>
          {showEmpty && (
            <Button onClick={handleSeed} disabled={seeding} variant="outline" data-testid="seed-defaults-btn">
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Database className="w-4 h-4 mr-2" />}
              Initialize Defaults
            </Button>
          )}
          <Button onClick={() => { fetchDashboard(); if (activeTab === 'dpo') fetchDPO(); }} variant="ghost" size="icon" data-testid="refresh-dashboard-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="dpdpa-dashboard-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="dpo" data-testid="tab-dpo">
            <UserCheck className="w-3.5 h-3.5 mr-1.5" /> DPO Dashboard
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6 mt-4">
          <OverviewTab data={data} />
        </TabsContent>

        <TabsContent value="dpo" className="space-y-6 mt-4">
          {dpoLoading ? (
            <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin opacity-50" /></div>
          ) : dpoData ? (
            <DPODashboardTab data={dpoData} />
          ) : (
            <div className="text-center py-12 opacity-50">No DPO data available</div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* ====== Overview Tab (original dashboard content) ====== */
function OverviewTab({ data }) {
  return (
    <>
      {/* Top Row: Compliance Score + Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-1">
          <CardContent className="p-4 flex items-center justify-center h-full">
            <ScoreGauge score={data?.compliance_score || 0} />
          </CardContent>
        </Card>
        <div className="lg:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={CheckCircle} label="Active Consents" value={data?.consents?.active || 0} sub={`${data?.consents?.total || 0} total`} color="text-emerald-500" testId="stat-active-consents" />
          <StatCard icon={Users} label="DSR Requests" value={data?.requests?.total || 0} sub={`${data?.requests?.pending || 0} pending`} color="text-blue-500" testId="stat-dsr-requests" />
          <StatCard icon={FileText} label="Active Purposes" value={data?.purposes?.active || 0} color="text-violet-500" testId="stat-active-purposes" />
          <StatCard icon={AlertTriangle} label="Erasure Requests" value={data?.requests?.erasure || 0} sub="High priority" color="text-red-500" testId="stat-erasure-requests" />
        </div>
      </div>

      {/* Breach Alert Banner */}
      {data?.breaches?.active > 0 && (
        <Card className="border-l-4 border-l-red-500 bg-red-500/5" data-testid="breach-alert-banner">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              <div>
                <p className="font-semibold text-red-700">{data.breaches.active} Active Data Breach{data.breaches.active > 1 ? 'es' : ''}</p>
                <p className="text-xs opacity-60">{data.breaches.critical > 0 ? `${data.breaches.critical} critical severity` : 'Requires attention per DPDPA Section 8(6)'} — 72h DPB notification deadline</p>
              </div>
            </div>
            <Button variant="outline" size="sm" className="text-red-600 border-red-300" onClick={() => window.location.href = '/grc/dpdpa/breaches'} data-testid="view-breaches-btn">
              View Breaches
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Score Breakdown */}
      {data?.score_breakdown && (
        <Card data-testid="score-breakdown-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Compliance Score Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {Object.entries(data.score_breakdown).map(([key, value]) => (
                <div key={key}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="font-medium">{value}%</span>
                  </div>
                  <Progress value={value} className="h-2" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Middle Row: DSR Breakdown + PII Scan + Jira */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="dsr-breakdown-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Users className="w-4 h-4" /> Requests by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data?.requests?.by_type && Object.entries(data.requests.by_type).map(([type, count]) => (
                <div key={type} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {type === 'erasure' ? <Trash2 className="w-4 h-4 text-red-400" /> :
                     type === 'access' ? <Eye className="w-4 h-4 text-blue-400" /> :
                     <FileText className="w-4 h-4 text-gray-400" />}
                    <span className="text-sm capitalize">{type}</span>
                  </div>
                  <Badge variant={count > 0 ? 'default' : 'secondary'}>{count}</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="pii-scan-summary-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Database className="w-4 h-4" /> PII Scan Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.pii_scan ? (
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="opacity-60">Collections Scanned</span><span className="font-medium">{data.pii_scan.total_collections}</span></div>
                <div className="flex justify-between"><span className="opacity-60">PII Fields Found</span><span className="font-medium text-amber-500">{data.pii_scan.total_pii_fields}</span></div>
                <div className="flex justify-between"><span className="opacity-60">Collections with PII</span><span className="font-medium">{data.pii_scan.collections_with_pii}</span></div>
                <div className="flex justify-between"><span className="opacity-60">Risk Collections</span><span className="font-medium text-red-500">{data.pii_scan.risk_collections}</span></div>
                <div className="flex justify-between"><span className="opacity-60">Last Scan</span><span className="font-medium">{new Date(data.pii_scan.scanned_at).toLocaleDateString()}</span></div>
              </div>
            ) : (
              <p className="text-sm opacity-50">No PII scan performed yet. Run a scan from the PII Scanner page.</p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="system-status-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> System Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <span className="opacity-60">Jira Integration</span>
                <Badge variant={data?.jira_configured ? 'default' : 'secondary'}>
                  {data?.jira_configured ? 'Connected' : 'Not Configured'}
                </Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="opacity-60">Active Alert Rules</span>
                <span className="font-medium">{data?.alert_rules?.active || 0}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="opacity-60">Consent Withdrawal Rate</span>
                <span className="font-medium">{data?.consents?.withdrawal_rate || 0}%</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Notifications */}
      {data?.notifications && data.notifications.length > 0 && (
        <Card data-testid="dpdpa-notifications-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Bell className="w-4 h-4" /> Recent Notifications</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.notifications.slice(0, 5).map((notif) => (
                <div key={notif.id} className={`flex items-start gap-3 p-3 rounded-lg ${notif.read ? 'opacity-60' : 'bg-amber-500/5 border border-amber-500/20'}`}>
                  <Bell className="w-4 h-4 mt-0.5 text-amber-500" />
                  <div>
                    <p className="text-sm font-medium">{notif.title}</p>
                    <p className="text-xs opacity-60">{notif.message}</p>
                    <p className="text-xs opacity-40 mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

/* ====== DPO Dashboard Tab ====== */
function DPODashboardTab({ data }) {
  const urgencyColor = (status) => {
    const m = { overdue: 'text-red-600 bg-red-500/10', critical: 'text-red-500 bg-red-500/10', urgent: 'text-orange-500 bg-orange-500/10', at_risk: 'text-amber-500 bg-amber-500/10', warning: 'text-amber-500 bg-amber-500/10', on_track: 'text-emerald-500 bg-emerald-500/10' };
    return m[status] || 'text-gray-500 bg-gray-500/10';
  };

  return (
    <div className="space-y-6" data-testid="dpo-dashboard">
      {/* Top Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Timer} label="Overdue DSRs" value={data?.dsr?.overdue || 0} sub={`${data?.dsr?.sla_at_risk || 0} at risk`} color="text-red-500" testId="dpo-stat-overdue-dsrs" />
        <StatCard icon={AlertTriangle} label="Active Breaches" value={data?.breaches?.active || 0} sub={`${data?.breaches?.overdue_notifications || 0} overdue`} color="text-orange-500" testId="dpo-stat-active-breaches" />
        <StatCard icon={FileWarning} label="Letters Pending Review" value={data?.letters?.pending_review || 0} color="text-violet-500" testId="dpo-stat-pending-letters" />
        <StatCard icon={TrendingUp} label="Withdrawal Rate (30d)" value={`${data?.consent_trends?.withdrawal_rate_30d || 0}%`} sub={`${data?.consent_trends?.withdrawn_last_30d || 0} withdrawn`} color="text-amber-500" testId="dpo-stat-withdrawal-rate" />
      </div>

      {/* DSR SLA + Breach Response Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* DSR SLA Tracking */}
        <Card data-testid="dpo-dsr-sla-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Timer className="w-4 h-4" /> DSR SLA Tracking</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 rounded-lg bg-amber-500/10">
                <p className="text-2xl font-bold text-amber-600">{data?.dsr?.pending || 0}</p>
                <p className="text-xs opacity-60">Pending</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/10">
                <p className="text-2xl font-bold text-blue-600">{data?.dsr?.in_progress || 0}</p>
                <p className="text-xs opacity-60">In Progress</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/10">
                <p className="text-2xl font-bold text-emerald-600">{data?.dsr?.avg_resolution_hours || 0}h</p>
                <p className="text-xs opacity-60">Avg Resolution</p>
              </div>
            </div>
            {/* Overdue DSRs list */}
            {data?.dsr?.overdue_list?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-red-600 mb-2 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Overdue Requests</p>
                <div className="space-y-1.5">
                  {data.dsr.overdue_list.slice(0, 5).map(dsr => (
                    <div key={dsr.id} className="flex items-center justify-between p-2 rounded bg-red-500/5 border border-red-500/10 text-sm" data-testid={`overdue-dsr-${dsr.id}`}>
                      <div className="flex items-center gap-2">
                        <Badge variant="destructive" className="text-[10px] px-1.5 py-0">{dsr.request_type}</Badge>
                        <span className="truncate max-w-[150px]">{dsr.principal_identifier}</span>
                      </div>
                      <span className="text-xs text-red-500 font-medium">{dsr.hours_overdue}h overdue</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* At-risk DSRs */}
            {data?.dsr?.at_risk_list?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-amber-600 mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> SLA At Risk (&lt;48h)</p>
                <div className="space-y-1.5">
                  {data.dsr.at_risk_list.slice(0, 5).map(dsr => (
                    <div key={dsr.id} className="flex items-center justify-between p-2 rounded bg-amber-500/5 border border-amber-500/10 text-sm">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-amber-500/20 text-amber-700 text-[10px] px-1.5 py-0">{dsr.request_type}</Badge>
                        <span className="truncate max-w-[150px]">{dsr.principal_identifier}</span>
                      </div>
                      <span className="text-xs text-amber-600 font-medium">{dsr.hours_remaining}h left</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Breach Response */}
        <Card data-testid="dpo-breach-response-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4" /> Breach Response Status</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="p-3 rounded-lg bg-red-500/10">
                <p className="text-2xl font-bold text-red-600">{data?.breaches?.active || 0}</p>
                <p className="text-xs opacity-60">Active</p>
              </div>
              <div className="p-3 rounded-lg bg-orange-500/10">
                <p className="text-2xl font-bold text-orange-600">{data?.breaches?.overdue_notifications || 0}</p>
                <p className="text-xs opacity-60">Overdue DPB</p>
              </div>
              <div className="p-3 rounded-lg bg-emerald-500/10">
                <p className="text-2xl font-bold text-emerald-600">{data?.breaches?.dpb_notified || 0}</p>
                <p className="text-xs opacity-60">DPB Notified</p>
              </div>
            </div>
            {data?.breaches?.active_list?.length > 0 && (
              <div className="space-y-1.5">
                {data.breaches.active_list.slice(0, 5).map(b => (
                  <div key={b.id} className="flex items-center justify-between p-2 rounded border text-sm" data-testid={`breach-item-${b.id}`}>
                    <div className="flex items-center gap-2">
                      <Badge className={b.severity === 'critical' ? 'bg-red-600 text-white' : b.severity === 'high' ? 'bg-orange-500 text-white' : 'bg-amber-500'} >{b.severity}</Badge>
                      <span className="truncate max-w-[140px]">{b.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {b.dpb_notified && <Send className="w-3 h-3 text-emerald-500" />}
                      <span className={`text-xs font-medium ${b.countdown?.status === 'overdue' ? 'text-red-500' : b.countdown?.status === 'critical' ? 'text-red-400' : 'text-amber-500'}`}>
                        {b.countdown?.status === 'notified' ? 'Notified' : b.countdown?.expired ? 'OVERDUE' : `${b.countdown?.hours_remaining || 0}h`}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Pending letters */}
            {data?.letters?.pending_list?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-violet-600 mb-2 flex items-center gap-1"><FileWarning className="w-3 h-3" /> Letters Awaiting Your Review</p>
                <div className="space-y-1.5">
                  {data.letters.pending_list.map(l => (
                    <div key={l.id} className="flex items-center justify-between p-2 rounded bg-violet-500/5 border border-violet-500/10 text-sm">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-violet-500/20 text-violet-700 text-[10px] px-1.5 py-0">{l.letter_type?.toUpperCase()} v{l.version}</Badge>
                        <span className="text-xs opacity-60">Breach {l.breach_id?.slice(0, 8)}</span>
                      </div>
                      <span className="text-xs opacity-50">{new Date(l.generated_at || l.created_at).toLocaleDateString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Consent Trends + Upcoming Deadlines */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Consent Trends */}
        <Card data-testid="dpo-consent-trends-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Consent Trends (30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 rounded-lg bg-emerald-500/10 text-center">
                <p className="text-2xl font-bold text-emerald-600">{data?.consent_trends?.new_last_30d || 0}</p>
                <p className="text-xs opacity-60">New Consents</p>
              </div>
              <div className="p-3 rounded-lg bg-red-500/10 text-center">
                <p className="text-2xl font-bold text-red-600">{data?.consent_trends?.withdrawn_last_30d || 0}</p>
                <p className="text-xs opacity-60">Withdrawals</p>
              </div>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="opacity-60">Total Active</span><span className="font-medium">{data?.consent_trends?.active || 0}</span></div>
              <div className="flex justify-between"><span className="opacity-60">Total Withdrawn</span><span className="font-medium">{data?.consent_trends?.withdrawn_total || 0}</span></div>
              <div className="flex justify-between"><span className="opacity-60">30-Day Withdrawal Rate</span>
                <span className={`font-medium ${(data?.consent_trends?.withdrawal_rate_30d || 0) > 20 ? 'text-red-500' : 'text-emerald-500'}`}>
                  {data?.consent_trends?.withdrawal_rate_30d || 0}%
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upcoming Deadlines */}
        <Card data-testid="dpo-deadlines-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Calendar className="w-4 h-4" /> Compliance Deadlines</CardTitle>
          </CardHeader>
          <CardContent>
            {data?.upcoming_deadlines?.length > 0 ? (
              <div className="space-y-2">
                {data.upcoming_deadlines.slice(0, 8).map((d, i) => (
                  <div key={i} className="flex items-center justify-between p-2 rounded border text-sm" data-testid={`deadline-${i}`}>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[10px] px-1.5 py-0 ${urgencyColor(d.status)}`}>{d.type === 'breach' ? 'BREACH' : 'DSR'}</Badge>
                      <span className="truncate max-w-[200px]">{d.title}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={`text-[10px] ${urgencyColor(d.status)}`}>{d.status?.replace('_', ' ')}</Badge>
                      {d.due_date && <span className="text-[10px] opacity-40">{new Date(d.due_date).toLocaleDateString()}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 opacity-50">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
                <p className="text-sm">No urgent deadlines</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
